import { Component, EventEmitter, Input, Output } from "@angular/core";
import { DispatchTripGridItem } from "@app/shared/models/DispatchTrip.model";
import { FeatureFlagsService } from "@app/agilx/shared/feature-flags.service";

export interface EmailPreviewItem {
  to: string;
  cc?: string;
  subject: string;
  body: string;
  driverName: string;
  referenceNumber: string;
  expanded: boolean;
  hasEmail: boolean;
}

@Component({
  selector: "app-email-preview-modal",
  templateUrl: "./email-preview-modal.component.html",
  styleUrls: ["./email-preview-modal.component.css"],
})
export class EmailPreviewModalComponent {
  constructor(private featureFlags: FeatureFlagsService) {}

  get emailFeaturesEnabled(): boolean {
    return this.featureFlags.isEnabled("emailFeatures");
  }

  @Input() visible: boolean = false;
  @Input() emails: EmailPreviewItem[] = [];
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() confirmed = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  get withEmailCount(): number {
    return this.emails.filter((e) => e.hasEmail).length;
  }

  get withoutEmailCount(): number {
    return this.emails.filter((e) => !e.hasEmail).length;
  }

  toggleExpand(item: EmailPreviewItem): void {
    item.expanded = !item.expanded;
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.cancelled.emit();
  }

  onConfirm(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.confirmed.emit();
  }

  onHide(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.cancelled.emit();
  }

  static buildEmailPreview(trip: DispatchTripGridItem, cc?: string): EmailPreviewItem {
    const startDate = trip.startDate
      ? new Date(trip.startDate).toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        })
      : "N/A";
    const driverName = trip.driverName || "Unknown";
    const subject = `${startDate} - ${driverName} - Dispatch Trip #${trip.referenceNumber}`;

    const body = `
<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto">
  <div style="background:#f8f9fa;border-bottom:3px solid #1976d2;padding:16px 20px;border-radius:6px 6px 0 0">
  </div>
  <table style="border-collapse:collapse;width:100%;border:1px solid #e0e0e0;border-top:none">
    <tr style="background:#ffffff"><td style="padding:10px 14px;font-weight:600;color:#555;width:140px;border-bottom:1px solid #f0f0f0">Load #:</td><td style="padding:10px 14px;border-bottom:1px solid #f0f0f0;color:#333">${trip.referenceNumber || "N/A"}</td></tr>
    <tr style="background:#f8f9fa"><td style="padding:10px 14px;font-weight:600;color:#555;width:140px;border-bottom:1px solid #f0f0f0">Customer:</td><td style="padding:10px 14px;border-bottom:1px solid #f0f0f0;color:#333">${trip.clientName || "N/A"}</td></tr>
    <tr style="background:#ffffff"><td style="padding:10px 14px;font-weight:600;color:#555;width:140px;border-bottom:1px solid #f0f0f0">Pick Up:</td><td style="padding:10px 14px;border-bottom:1px solid #f0f0f0;color:#333">${trip.loadLocationName || "N/A"}</td></tr>
    <tr style="background:#f8f9fa"><td style="padding:10px 14px;font-weight:600;color:#555;width:140px;border-bottom:1px solid #f0f0f0">Unload Location:</td><td style="padding:10px 14px;border-bottom:1px solid #f0f0f0;color:#333">${trip.unloadLocationName || "N/A"}</td></tr>
    <tr style="background:#ffffff"><td style="padding:10px 14px;font-weight:600;color:#555;width:140px;border-bottom:1px solid #f0f0f0">Delivery Window:</td><td style="padding:10px 14px;border-bottom:1px solid #f0f0f0;color:#333">${trip.deliveryWindow || "N/A"}</td></tr>
    <tr style="background:#f8f9fa"><td style="padding:10px 14px;font-weight:600;color:#555;width:140px;border-bottom:1px solid #f0f0f0">Start Date:</td><td style="padding:10px 14px;border-bottom:1px solid #f0f0f0;color:#333">${startDate}</td></tr>
    <tr style="background:#ffffff"><td style="padding:10px 14px;font-weight:600;color:#555;width:140px;border-bottom:1px solid #f0f0f0">BOL:</td><td style="padding:10px 14px;border-bottom:1px solid #f0f0f0;color:#333">${trip.bolNumber || "N/A"}</td></tr>
  </table>
</div>`;

    return {
      to: trip.driverEmail || "",
      cc: cc || undefined,
      subject,
      body,
      driverName,
      referenceNumber: trip.referenceNumber,
      expanded: false,
      hasEmail: !!trip.driverEmail,
    };
  }
}
